import { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { appConfigVariableValues } from '../../../../../utils/constants';
import colors from '../../../../../scss/colors';

function PeriodCard({ periodLabel, summaryLine, isExpense, withSubNote = false }) {
  return (
    <div className="w-full bg-gray-50 rounded-xl p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
        <AccessTimeIcon sx={{ color: 'white', fontSize: 20 }} />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#1a1a2e] m-0">{periodLabel}</p>
        <p className="text-xs text-gray-500 mt-0.5 m-0">
          {summaryLine}
          {withSubNote && !isExpense && ' | what you see on screen'}
        </p>
      </div>
    </div>
  );
}

export default function ExportModal({ timesheet, expense, periodLabel, onClose }) {
  const { DOMAIN, BLOB_CONTAINER_TNE, BLOB_CONTAINER, SASS_TOKEN } = appConfigVariableValues;

  const source = timesheet || expense;
  const isExpense = !!expense;

  const getDetails = () => {
    if (isExpense) return source?.ExpenseDetails || [];
    if (source?.IsSplitApprovalFlow) return source?.TimesheetSections?.flatMap((s) => s.TimesheetDetails || []);
    return source?.TimesheetDetails || [];
  };
  const details = getDetails();

  const days = isExpense
    ? []
    : (source?.IsSplitApprovalFlow
        ? source?.TimesheetSections?.[0]?.TimesheetDetails?.[0]
        : source?.TimesheetDetails?.[0]
      )?.TimesheetHoursByDates || [];

  const defaultFileName = (() => {
    if (days.length) {
      const start = days[0].Day.split('T')[0];
      const end = days.at(-1).Day.split('T')[0];
      return `Timesheet_${start}_to_${end}.pdf`;
    }
    if (isExpense) return `Expense_${source?.Id}.pdf`;
    return `Timesheet_${source?.Id}.pdf`;
  })();

  const summaryLine = isExpense
    ? `${details.length} claims`
    : `${details.reduce(
        (sum, d) => sum + (d.TimesheetHoursByDates || []).reduce((s, h) => s + Number(h.TotalHrs || 0), 0),
        0
      )} h total | ${details.length} entries`;

  const downloadUrl = source?.ExportFileAttachmentPath
    ? `${DOMAIN}/${isExpense ? BLOB_CONTAINER : BLOB_CONTAINER_TNE}/${encodeURIComponent(
        source.ExportFileAttachmentPath
      )}${SASS_TOKEN}`
    : null;

  const [step, setStep] = useState('confirm'); // confirm | loading | ready
  const [fileName, setFileName] = useState(defaultFileName);
  const [downloadError, setDownloadError] = useState(false);

  const triggerDownload = async () => {
    if (!downloadUrl) return;
    setDownloadError(false);
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Download failed:', e);
      setDownloadError(true);
    }
  };

  const handleExport = async () => {
    setStep('loading');
    await triggerDownload();
    setStep('ready');
  };

  if (step === 'loading') {
    return (
      <div className="flex flex-col items-center gap-5 px-8 py-12 w-full md:min-w-[400px]">
        <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
          <CircularProgress size={28} sx={{ color: '#1B6CA8' }} />
        </div>
        <p className="text-xl font-bold text-[#1a1a2e] m-0">Generating your export...</p>
        <p className="text-sm text-gray-500 text-center m-0 max-w-xs">
          Preparing a PDF of your all time {`${isExpense ? 'expenses' : 'timesheets'}`}. This usually takes a few
          seconds.
        </p>
      </div>
    );
  }

  if (step === 'ready') {
    return (
      <div className="flex flex-col items-center gap-5 px-8 py-12 w-full md:min-w-[400px]">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircleOutlineIcon sx={{ color: '#2ecc71', fontSize: 32 }} />
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-[#1a1a2e] m-0">Your export is ready</p>
          <p className="text-sm text-gray-500 mt-2 m-0 max-w-sm">
            {downloadError
              ? 'Something went wrong during download. Please try again.'
              : `We've generated a PDF of your ${
                  isExpense ? 'expenses' : 'timesheets'
                } and started the download. You can find it in your browser's downloads.`}
          </p>
        </div>
        <PeriodCard periodLabel={periodLabel} summaryLine={summaryLine} isExpense={isExpense} />
        <button
          onClick={triggerDownload}
          className="flex items-center gap-2 bg-[#1a1a2e] text-white font-semibold text-sm px-6 py-2.5 rounded-full hover:opacity-90 transition-opacity cursor-pointer border-none mt-2"
        >
          <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          Download again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-7 w-full md:min-w-[460px]">
      <div>
        <p className="text-xl font-bold text-navy-blue m-0">Export {isExpense ? 'Expense' : 'Timesheet'}</p>
        <p className="text-sm text-gray-500 mt-1 m-0">Download a copy for tax or personal record keeping.</p>
      </div>

      <PeriodCard periodLabel={periodLabel} summaryLine={summaryLine} isExpense={isExpense} withSubNote />

      <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
        <InsertDriveFileOutlinedIcon sx={{ color: colors.BLUEISH_GREY, fontSize: 22 }} />
        <p className="text-sm text-[#1a1a2e] m-0">
          <span className="font-semibold">PDF</span> - formatted report
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider m-0">File Name</p>
        <input
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-[#1a1a2e] outline-none focus:border-[#1a1a2e] transition-colors"
        />
      </div>

      <div className="flex flex-col gap-4 md:flex-row items-center justify-between mt-1">
        <p className="text-xs text-gray-500 m-0">✓ Will download to your device</p>
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="border border-gray-200 rounded-full px-5 py-2 text-sm font-medium text-[#1a1a2e] bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!downloadUrl}
            className={`flex items-center gap-1.5 bg-secondary text-white rounded-full px-5 py-2 text-sm font-semibold border-none transition-opacity
              ${downloadUrl ? 'opacity-100 cursor-pointer hover:opacity-90' : 'opacity-40 cursor-not-allowed'}`}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
            Export
          </button>
        </div>
      </div>
    </div>
  );
}
